import Dropdown from "@shared/components/dropdown/Dropdown";
import theme from "@shared/utils/themeColors";
import EmojiPicker from "emoji-picker-react";
import GifPicker, { ContentFilter } from "gif-picker-react";
import React, { useEffect, useRef, useState } from "react";
import { useMessageMedia } from "../hooks/useMessageMedia";
import MessageMediaPreview from "./MessageMediaPreview";

interface MessageInputProps {
  onSendMessage: (
    content: string,
    type: "text" | "image" | "video" | "gif",
    mediaUrl?: string,
  ) => void;
  disabled?: boolean;
  updateTypingStatus: (isTyping: boolean) => void;
}

const MessageInput: React.FC<MessageInputProps> = ({
  onSendMessage,
  disabled,
  updateTypingStatus,
}) => {
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState({
    bottom: 0,
    right: 0,
    left: 0,
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputBarRef = useRef<HTMLDivElement>(null);
  const { mediaPreview, handleFileSelect, handleGifSelect, removeMedia } =
    useMessageMedia();

  const updatePickerPosition = () => {
    if (inputBarRef.current) {
      const rect = inputBarRef.current.getBoundingClientRect();
      setPickerPosition({
        bottom: window.innerHeight - rect.top,
        right: window.innerWidth - rect.right,
        left: rect.left,
      });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const emojiPicker = document.querySelector(".EmojiPickerReact");
      const gifPicker = document.querySelector(".GifPickerReact");

      if (emojiPicker && !emojiPicker.contains(target)) {
        setShowEmojiPicker(false);
      }

      if (gifPicker && !gifPicker?.contains(target)) {
        setShowGifPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage();
  };

  const handleSendMessage = () => {
    if (mediaPreview) {
      if (
        (!mediaPreview.error && mediaPreview.progress === 100) ||
        mediaPreview.type === "gif"
      ) {
        onSendMessage(
          message || mediaPreview.message || "",
          mediaPreview.type,
          mediaPreview.url,
        );
        removeMedia();
        setMessage("");
      }
    } else if (message.trim()) {
      onSendMessage(message.trim(), "text");
      setMessage("");
    }
  };

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      await handleFileSelect(file);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleGifSelection = async (gif: { url: string }) => {
    const result = handleGifSelect(gif.url, message);
    setShowGifPicker(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setMessage(value);
    updateTypingStatus(value.length > 0);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const myItems = [
    {
      id: "multimedia",
      label: "Send Image/Video",
      onClick: () => fileInputRef.current?.click(),
    },
    {
      id: "gif",
      label: "Send GIF",
      onClick: () => {
        updatePickerPosition();
        setShowGifPicker(true);
      },
    },
  ];

  return (
    <div className="sticky bottom-0">
      {/* Media Preview */}
      {mediaPreview && (
        <div
          className={`bg-white dark:bg-gray-900 border-t ${theme.border.primary} px-4 py-2 flex items-center`}
        >
          <MessageMediaPreview
            media={mediaPreview}
            onRemove={() => removeMedia()}
          />
        </div>
      )}

      <div
        ref={inputBarRef}
        className={`flex items-center justify-between bg-white dark:bg-gray-900 border-t ${theme.border.primary} px-4 sm:px-6 md:px-5 h-16`}
      >
        {/* Plus button */}
        <Dropdown
          horizontalAlign="left"
          verticalAlign="top"
          title="Add"
          items={myItems}
          trigger={
            <svg className="w-6 h-6 fill-current" viewBox="0 0 24 24">
              <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12C23.98 5.38 18.62.02 12 0zm6 13h-5v5h-2v-5H6v-2h5V6h2v5h5v2z" />
            </svg>
          }
          className="mr-4"
        />
        <form className="grow flex" onSubmit={handleSubmit}>
          <div className="grow mr-3 relative">
            <label htmlFor="message-input" className="sr-only">
              Type a message
            </label>
            <textarea
              id="message-input"
              className={`form-input w-full pr-8 ${theme.bg.inactive} border-transparent dark:border-transparent focus:bg-white dark:focus:bg-gray-800 placeholder-gray-500 h-[38px] mt-1`}
              autoComplete="off"
              value={message}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              disabled={disabled}
            />
            {/* Emoji Button */}
            <div className="absolute inset-0 left-auto flex items-center">
              <button
                className="inline-flex items-center text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200"
                type="button"
                onClick={() => {
                  updatePickerPosition();
                  setShowEmojiPicker(!showEmojiPicker);
                  setShowGifPicker(false);
                }}
                disabled={disabled}
              >
                <svg
                  className={`mr-2 ${theme.bg.fill}`}
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M9.793 10.002a.5.5 0 0 1 .353.853l-1.792 1.793a.5.5 0 0 1-.708 0l-1.792-1.793a.5.5 0 0 1 .353-.853h3.586Zm5.014-4.63c1.178 2.497 1.833 5.647.258 7.928-1.238 1.793-3.615 2.702-7.065 2.702S2.173 15.092.935 13.3c-1.575-2.28-.92-5.431.258-7.927A2.962 2.962 0 0 1 0 3.002a3 3 0 0 1 3-3c.787 0 1.496.309 2.029.806a5.866 5.866 0 0 1 5.942 0A2.96 2.96 0 0 1 13 .002a3 3 0 0 1 3 3c0 .974-.472 1.827-1.193 2.37Zm-1.387 6.79c1.05-1.522.417-3.835-.055-5.078C12.915 5.89 11.192 2.002 8 2.002s-4.914 3.89-5.365 5.082c-.472 1.243-1.106 3.556-.055 5.079.843 1.22 2.666 1.839 5.42 1.839s4.577-.62 5.42-1.84ZM6.67 6.62c.113.443.102.68-.433 1.442-.535.761-1.06 1.297-1.658 1.297-.597 0-1.08-.772-1.07-1.483.01-.71.916-2.306 1.997-2.306.784 0 1.05.607 1.163 1.05Zm3.824-1.05c1.08 0 1.987 1.596 1.997 2.306.01.71-.473 1.483-1.07 1.483-.598 0-1.123-.536-1.658-1.297-.535-.762-.546-1-.432-1.442.113-.443.38-1.05 1.163-1.05Z" />
                </svg>
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="inline-flex items-center justify-center font-medium rounded-full transition-colors duration-150 ease-in-out text-xs px-4 py-1.5 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            disabled={
              (!message.trim() && !mediaPreview) ||
              disabled ||
              (mediaPreview?.progress !== undefined &&
                mediaPreview.progress < 100)
            }
          >
            Send
          </button>
        </form>
      </div>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/*,video/*"
        onChange={handleMediaUpload}
      />

      {/* Emoji Picker */}
      {showEmojiPicker && (
        <div
          className="fixed z-[9999]"
          style={{
            bottom: pickerPosition.bottom,
            right: pickerPosition.right,
          }}
        >
          <EmojiPicker
            height={450}
            width={350}
            onEmojiClick={(emojiData) => {
              setMessage((prev) => prev + emojiData.emoji);
              setShowEmojiPicker(false);
            }}
          />
        </div>
      )}

      {/* GIF Picker */}
      {showGifPicker && (
        <div
          className="fixed z-[9999]"
          style={{
            bottom: pickerPosition.bottom,
            left: pickerPosition.left,
          }}
        >
          <GifPicker
            onGifClick={handleGifSelection}
            tenorApiKey={import.meta.env.VITE_TENOR_API_KEY}
            autoFocusSearch={true}
            contentFilter={ContentFilter.HIGH}
            width={350}
          />
        </div>
      )}
    </div>
  );
};

export default MessageInput;
